import type { UserRole } from "../types";

/** A record opened from search, kept so an empty search box can offer it again. */
export interface RecentItem {
  kind: "issue" | "task" | "team" | "release";
  id: string;
  title: string;
  /** Issue key, team key, or release tag shown before the title. */
  key?: string;
  to: string;
}

export const RECENT_LIMIT = 5;

/** Puts `item` first, dropping an older copy, and keeps the newest `limit`. */
export function rememberRecent(list: RecentItem[], item: RecentItem, limit = RECENT_LIMIT): RecentItem[] {
  const rest = list.filter((entry) => !(entry.kind === item.kind && entry.id === item.id));
  return [item, ...rest].slice(0, limit);
}

/**
 * Whether the role can still open a remembered record. Recents live in the
 * browser, so a role change must not leave links to pages that now refuse it.
 */
export function recentAllowed(item: RecentItem, role: UserRole | undefined): boolean {
  switch (item.kind) {
    case "issue":
      return true;
    case "team":
      return role === "admin" || role === "manager" || role === "user";
    case "release":
      return role === "admin" || role === "manager";
    case "task":
      return role === "admin";
  }
}

/** Names what the role can find, so the box never promises records it will not return. */
export function searchPlaceholder(role: UserRole | undefined): string {
  if (role === "admin") return "Search issues, work orders, teams, people…";
  if (role === "manager") return "Search issues, teams, people, releases…";
  if (role === "user") return "Search issues, teams, people, labels…";
  return "Search issues, people, labels…";
}

/** A navigation label matches when one of its words starts with every word of the query. */
export function matchesLabel(label: string, query: string): boolean {
  const words = label.toLowerCase().split(/\s+/);
  const terms = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
  if (!terms.length) return true;
  return terms.every((term) => words.some((word) => word.startsWith(term)));
}

/** Cmd-K on a Mac, Ctrl-K elsewhere. Either works on both, as in most web apps. */
export function isCommandMenuShortcut(
  event: Pick<KeyboardEvent, "key" | "metaKey" | "ctrlKey" | "altKey" | "shiftKey">,
): boolean {
  return (
    event.key.toLowerCase() === "k" && (event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey
  );
}

export const IS_MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
